// The seat drawer's Responsibilities section (M1-05; Setup › Seats until the seat page of M1-08
// exists): what the seat is responsible for, with each responsibility's weight, status and
// linked KPI count, and under each one its recurring work. Managers add, edit, archive or
// restore and delete responsibilities (archiving pauses its active recurring work), and add
// recurring work through a small schedule builder that writes the RRULE (rrule.ts), pause and
// resume it, delete it and run it now. A run creates a task when the recurring work names a
// board (M2-03's hook) and only records the occurrence otherwise; the drawer says which.
// Everyone else reads.
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldDescription,
  FieldLabel,
  FieldTitle,
  Input,
  NativeSelect,
  NativeSelectOption,
  Notice,
  QuickAddDialog,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  Toggle,
  toast,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PlusIcon } from "lucide-react";
import { type FormEvent, useState } from "react";
import type { Api } from "../../api/client";
import type { RecurringWork, Responsibility } from "../../api/types";
import { chartKeys } from "../chart/chart-queries";
import type { CompanyAccess } from "../company";
import { orgKeys, recurringWorkQuery, responsibilitiesQuery } from "../queries";
import { Loading } from "../screen-parts";
import { boardsQuery } from "../work/work-queries";
import { ConfirmButton, ErrorNotice, FormError, MoreRows, problemCode } from "./parts";
import {
  type Builder,
  builderRule,
  builderText,
  describeSchedule,
  firstRun,
  formatInZone,
  WEEKDAY_LONG,
  WEEKDAY_SHORT,
  WEEKDAYS,
  type Weekday,
} from "./rrule";

/** What "Run now" did: a run makes a task only when the recurring work names a board (M2-03). */
export const TASK_CREATED_NOTE = "A task was created on its board.";
export const NO_TASK_NOTE =
  "No task was created: this recurring work has no board. Add it again with a board to get a task each run.";
export const runNowNote = (boardId: string | null | undefined) =>
  boardId ? TASK_CREATED_NOTE : NO_TASK_NOTE;

const tzOf = (company: CompanyAccess) => company.calendar?.timezone ?? "UTC";

/** SEAT-DRAWER-MODALS: a measurable a responsibility may link (the seat's KPIs). */
export type LinkableMeasurable = { id: string; name: string };

export function SeatResponsibilities({
  api,
  company,
  seatId,
  readOnly,
  measurables,
}: {
  api: Api;
  company: CompanyAccess;
  seatId: string;
  /** Not a manager, or the seat is closed. */
  readOnly: boolean;
  /** The seat's measurables, offered to link in the add and edit dialogs (absent: not offered). */
  measurables?: readonly LinkableMeasurable[] | undefined;
}) {
  const q = useInfiniteQuery(responsibilitiesQuery(api, company.id, seatId));
  const [adding, setAdding] = useState(false);
  const all = q.data?.pages.flatMap((p) => p.data) ?? [];
  // Active first, then archived; each in the order they were added.
  const shown = [
    ...all.filter((r) => r.status === "active"),
    ...all.filter((r) => r.status !== "active"),
  ];
  return (
    <div className="flex flex-col gap-4" data-slot="responsibilities">
      <div className="flex flex-wrap items-center gap-3">
        <p className="min-w-0 flex-1 text-sm text-muted-foreground">
          What this seat answers for. Each responsibility can carry recurring work on a schedule in
          the company's time zone ({tzOf(company)}).
        </p>
        {readOnly ? null : (
          <Button size="sm" variant="secondary" onClick={() => setAdding(true)}>
            <PlusIcon data-icon="inline-start" />
            Add responsibility
          </Button>
        )}
      </div>
      {q.isPending ? (
        <Loading label="Loading responsibilities" rows={2} />
      ) : q.isError ? (
        <ErrorNotice what="Responsibilities" error={q.error} />
      ) : !shown.length ? (
        <p className="text-sm text-muted-foreground">No responsibilities yet.</p>
      ) : (
        shown.map((r) => (
          <ResponsibilityItem
            key={r.id}
            api={api}
            company={company}
            responsibility={r}
            readOnly={readOnly}
            measurables={measurables}
          />
        ))
      )}
      <MoreRows what="responsibilities" q={q} />
      {adding ? (
        <ResponsibilityDialog
          api={api}
          co={company.id}
          seatId={seatId}
          measurables={measurables}
          onClose={() => setAdding(false)}
        />
      ) : null}
    </div>
  );
}

function useResponsibilityWrites(co: string, seatId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: orgKeys.responsibilities(co, seatId) });
    void qc.invalidateQueries({ queryKey: orgKeys.allRecurringWork(co) });
    // The chart's cards and the seat panel's setup count read responsibilities too.
    void qc.invalidateQueries({ queryKey: chartKeys.orgChart(co) });
  };
}

function ResponsibilityItem({
  api,
  company,
  responsibility: r,
  readOnly,
  measurables,
}: {
  api: Api;
  company: CompanyAccess;
  responsibility: Responsibility;
  readOnly: boolean;
  measurables?: readonly LinkableMeasurable[] | undefined;
}) {
  const co = company.id;
  const refresh = useResponsibilityWrites(co, r.seat_id);
  const [editing, setEditing] = useState(false);
  const setStatus = useMutation({
    mutationFn: (status: Responsibility["status"]) =>
      api.client.responsibilities.update({ params: { co, id: r.id }, body: { status } }),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: () => api.client.responsibilities.delete({ params: { co, id: r.id } }),
    onSuccess: refresh,
  });
  const archived = r.status === "archived";
  const kpis = r.kpi_metric_ids.length;
  return (
    <section
      aria-label={r.name}
      data-responsibility={r.id}
      className="flex flex-col gap-3 rounded-md border border-border p-4"
    >
      <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
        <div className="min-w-0 flex-1">
          <h3 className="font-medium text-foreground">{r.name}</h3>
          {r.description ? (
            <p className="mt-1 text-sm text-muted-foreground">{r.description}</p>
          ) : null}
          <p className="mt-1 text-xs text-muted-foreground tabular-nums">
            Weight {Number(r.weight)} · {kpis === 1 ? "1 linked KPI" : `${kpis} linked KPIs`}
          </p>
        </div>
        <Badge variant={archived ? "neutral" : "success"}>{archived ? "Archived" : "Active"}</Badge>
      </div>
      {readOnly ? null : (
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
            Edit<span className="sr-only"> {r.name}</span>
          </Button>
          {archived ? (
            <Button
              size="sm"
              variant="ghost"
              disabled={setStatus.isPending}
              onClick={() => setStatus.mutate("active")}
            >
              Restore<span className="sr-only"> {r.name}</span>
            </Button>
          ) : (
            <ConfirmButton
              label="Archive"
              srLabel={r.name}
              variant="ghost"
              destructive={false}
              title={`Archive "${r.name}"?`}
              description="Its active recurring work is paused. Restoring the responsibility later does not resume it: resume each schedule yourself."
              confirmLabel="Archive"
              pending={setStatus.isPending}
              onConfirm={() => setStatus.mutateAsync("archived")}
            />
          )}
          <ConfirmButton
            label="Delete"
            srLabel={r.name}
            variant="ghost"
            title={`Delete "${r.name}"?`}
            description="The responsibility and all its recurring work are removed. Archive it instead to keep its history."
            confirmLabel="Delete"
            pending={remove.isPending}
            onConfirm={() => remove.mutateAsync()}
          />
        </div>
      )}
      <FormError what="Changing the responsibility" error={setStatus.error} />
      <RecurringWorkList api={api} company={company} responsibility={r} readOnly={readOnly} />
      {editing ? (
        <ResponsibilityDialog
          api={api}
          co={co}
          seatId={r.seat_id}
          current={r}
          measurables={measurables}
          onClose={() => setEditing(false)}
        />
      ) : null}
    </section>
  );
}

/**
 * New or edit a responsibility, for the seat given (MODALS: the seat page's tab and the chart
 * panel's "Add a responsibility" open the same dialog, so the person stays where they are).
 */
export function ResponsibilityDialog({
  api,
  co,
  seatId,
  seatTitle,
  current,
  measurables,
  onClose,
}: {
  api: Api;
  co: string;
  seatId: string;
  /** The seat's title, when the dialog opens away from the seat's own page. */
  seatTitle?: string;
  /** Editing this one; absent = a new responsibility. */
  current?: Responsibility;
  /** SEAT-DRAWER-MODALS: the seat's measurables to link (absent: the links are left as they are). */
  measurables?: readonly LinkableMeasurable[] | undefined;
  onClose: () => void;
}) {
  const refresh = useResponsibilityWrites(co, seatId);
  const [name, setName] = useState(current?.name ?? "");
  const [description, setDescription] = useState(current?.description ?? "");
  const [weight, setWeight] = useState(current ? String(Number(current.weight)) : "1");
  const [linked, setLinked] = useState<readonly string[]>(current?.kpi_metric_ids ?? []);
  // The API's numeric(6,3): ≥ 0, < 1000, at most 3 fraction digits.
  const weightOk = /^\d{1,3}(\.\d{1,3})?$/.test(weight.trim());
  // A link to a metric no longer on the seat stays (it is not offered, so it is not unticked).
  const linksChanged =
    measurables !== undefined &&
    (linked.length !== (current?.kpi_metric_ids.length ?? 0) ||
      linked.some((id) => !current?.kpi_metric_ids.includes(id)));
  const save = useMutation({
    mutationFn: async () => {
      const body = {
        name: name.trim(),
        description: description.trim() || null,
        weight: weight.trim(),
        ...(linksChanged ? { kpi_metric_ids: [...linked] } : {}),
      };
      if (!current) return api.client.responsibilities.create({ params: { co, id: seatId }, body });
      return api.client.responsibilities.update({
        params: { co, id: current.id },
        body: {
          ...(body.name !== current.name ? { name: body.name } : {}),
          ...(body.description !== current.description ? { description: body.description } : {}),
          ...(Number(body.weight) !== Number(current.weight) ? { weight: body.weight } : {}),
          ...(body.kpi_metric_ids ? { kpi_metric_ids: body.kpi_metric_ids } : {}),
        },
      });
    },
    onSuccess: () => {
      refresh();
      toast.add({
        title: current ? "Responsibility saved" : "Responsibility added",
        type: "success",
      });
      onClose();
    },
  });
  const title = current ? "Edit responsibility" : "New responsibility";
  return (
    <QuickAddDialog
      title={title}
      info="What this seat answers for. Its weight sets how much it counts against the seat's other responsibilities."
      {...(seatTitle ? { description: `For ${seatTitle}` } : {})}
      submitLabel={current ? "Save responsibility" : "Add responsibility"}
      canSubmit={!!name.trim() && weightOk}
      pending={save.isPending}
      onSubmit={() => save.mutate()}
      onClose={onClose}
      slot="responsibility-dialog"
      error={
        <FormError
          what={current ? "Saving the responsibility" : "Adding the responsibility"}
          error={save.error}
        />
      }
    >
      <Field>
        <FieldLabel htmlFor="responsibility-name">Name</FieldLabel>
        <Input
          id="responsibility-name"
          value={name}
          required
          maxLength={200}
          onChange={(e) => setName(e.target.value)}
        />
      </Field>
      <Field>
        <FieldLabel htmlFor="responsibility-description">Description</FieldLabel>
        <Textarea
          id="responsibility-description"
          rows={3}
          maxLength={2000}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field>
      <Field>
        <FieldLabel htmlFor="responsibility-weight">Weight</FieldLabel>
        <Input
          id="responsibility-weight"
          inputMode="decimal"
          value={weight}
          aria-invalid={!weightOk || undefined}
          onChange={(e) => setWeight(e.target.value)}
        />
        <FieldDescription>0 to 999.999; 1 is the usual weight.</FieldDescription>
      </Field>
      {measurables?.length ? (
        <fieldset className="flex flex-col gap-1.5" data-slot="responsibility-measurables">
          <legend className="mb-1 text-sm font-medium">Measured by</legend>
          {measurables.map((m) => {
            const boxId = `responsibility-kpi-${m.id}`;
            return (
              <span key={m.id} className="flex items-center gap-2 text-sm">
                <Checkbox
                  id={boxId}
                  checked={linked.includes(m.id)}
                  onCheckedChange={(on) =>
                    setLinked((l) =>
                      on === true ? [...new Set([...l, m.id])] : l.filter((x) => x !== m.id),
                    )
                  }
                />
                <label htmlFor={boxId} className="min-w-0 truncate">
                  {m.name}
                </label>
              </span>
            );
          })}
        </fieldset>
      ) : null}
    </QuickAddDialog>
  );
}

// Recurring work -------------------------------------------------------------------------------

function RecurringWorkList({
  api,
  company,
  responsibility,
  readOnly,
}: {
  api: Api;
  company: CompanyAccess;
  responsibility: Responsibility;
  readOnly: boolean;
}) {
  const co = company.id;
  const qc = useQueryClient();
  const q = useInfiniteQuery(recurringWorkQuery(api, co, responsibility.id));
  const [adding, setAdding] = useState(false);
  const [ran, setRan] = useState<{
    id: string;
    due: string | null;
    boardId: string | null;
  } | null>(null);
  const refresh = () =>
    void qc.invalidateQueries({ queryKey: orgKeys.recurringWork(co, responsibility.id) });
  const setStatus = useMutation({
    mutationFn: (v: { id: string; status: RecurringWork["status"] }) =>
      api.client.recurringWork.update({ params: { co, id: v.id }, body: { status: v.status } }),
    onSuccess: refresh,
  });
  const runNow = useMutation({
    mutationFn: (w: RecurringWork) =>
      api.client.recurringWork.materialiseNow({ params: { co, id: w.id } }),
    onMutate: () => setRan(null),
    onSuccess: (_row, w) => {
      setRan({ id: w.id, due: w.next_run_at, boardId: w.board_id });
      refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.client.recurringWork.delete({ params: { co, id } }),
    onSuccess: refresh,
  });
  const rows = q.data?.pages.flatMap((p) => p.data) ?? [];
  const tz = tzOf(company);
  const canAdd = !readOnly && responsibility.status === "active";
  const statusProblem = problemCode(setStatus.error);
  const ranRow = ran ? rows.find((w) => w.id === ran.id) : undefined;
  return (
    <div className="flex flex-col gap-2" data-slot="recurring-work">
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="min-w-0 flex-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Recurring work
        </h4>
        {canAdd ? (
          <Button size="sm" variant="ghost" onClick={() => setAdding(true)}>
            <PlusIcon data-icon="inline-start" />
            Add recurring work<span className="sr-only"> to {responsibility.name}</span>
          </Button>
        ) : null}
      </div>
      {q.isPending ? (
        <Loading label="Loading recurring work" rows={1} />
      ) : q.isError ? (
        <ErrorNotice what="Recurring work" error={q.error} />
      ) : !rows.length ? (
        <p className="text-sm text-muted-foreground">No recurring work.</p>
      ) : (
        <Table>
          <TableCaption className="sr-only">Recurring work of {responsibility.name}</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead scope="col">Task</TableHead>
              <TableHead scope="col">Schedule</TableHead>
              <TableHead scope="col">Next run</TableHead>
              <TableHead scope="col">Status</TableHead>
              {readOnly ? null : (
                <TableHead scope="col">
                  <span className="sr-only">Actions</span>
                </TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((w) => {
              const paused = w.status === "paused";
              const title = w.task_template.title;
              return (
                <TableRow key={w.id} data-recurring={w.id}>
                  <TableHead scope="row" className="align-top">
                    <span className="font-medium">{title}</span>
                    {w.task_template.due_in_days !== undefined ? (
                      <span className="block text-xs font-normal text-muted-foreground">
                        Due{" "}
                        {w.task_template.due_in_days === 0
                          ? "the same day"
                          : `${w.task_template.due_in_days} d after`}
                      </span>
                    ) : null}
                  </TableHead>
                  <TableCell className="align-top">
                    <span className="block">{describeSchedule(w.schedule)}</span>
                    <code
                      className="block break-all font-mono text-xs text-muted-foreground"
                      title="RRULE"
                    >
                      {w.schedule}
                    </code>
                  </TableCell>
                  <TableCell className="align-top tabular-nums">
                    {paused || !w.next_run_at ? "—" : formatInZone(w.next_run_at, tz)}
                    {w.last_materialised_at ? (
                      <span className="block text-xs text-muted-foreground">
                        Last run {formatInZone(w.last_materialised_at, tz)}
                      </span>
                    ) : null}
                  </TableCell>
                  <TableCell className="align-top">
                    <Badge variant={paused ? "warning" : "success"}>
                      {paused ? "Paused" : "Active"}
                    </Badge>
                  </TableCell>
                  {readOnly ? null : (
                    <TableCell className="align-top">
                      <div className="flex flex-wrap justify-end gap-1">
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={paused || runNow.isPending}
                          onClick={() => runNow.mutate(w)}
                        >
                          Run now<span className="sr-only"> {title}</span>
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={setStatus.isPending}
                          onClick={() =>
                            setStatus.mutate({ id: w.id, status: paused ? "active" : "paused" })
                          }
                        >
                          {paused ? "Resume" : "Pause"}
                          <span className="sr-only"> {title}</span>
                        </Button>
                        <ConfirmButton
                          label="Delete"
                          srLabel={title}
                          variant="ghost"
                          title={`Delete "${title}"?`}
                          description="The schedule is removed; nothing it recorded is undone."
                          confirmLabel="Delete"
                          pending={remove.isPending}
                          onConfirm={() => remove.mutateAsync(w.id)}
                        />
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      <MoreRows what="recurring work" q={q} />
      {ran ? (
        <Notice tone="info" role="status" data-slot="run-now-note" title="Run recorded">
          {ranRow ? `"${ranRow.task_template.title}"` : "The schedule"} ran
          {ran.due ? ` for ${formatInZone(ran.due, tz)}` : ""}. {runNowNote(ran.boardId)}
        </Notice>
      ) : null}
      {statusProblem === "conflict" ? (
        <Notice tone="warning" role="alert" title="Cannot resume">
          {responsibility.status === "archived"
            ? "This responsibility is archived: restore it first, then resume its schedules."
            : "The seat or the responsibility no longer allows this schedule to run."}
        </Notice>
      ) : (
        <FormError what="Changing the schedule" error={setStatus.error} />
      )}
      <FormError what="Running it now" error={runNow.error} />
      {adding ? (
        <RecurringWorkDialog
          api={api}
          company={company}
          responsibility={responsibility}
          onClose={() => setAdding(false)}
          onSaved={refresh}
        />
      ) : null}
    </div>
  );
}

const MONTH_DAYS = [...Array.from({ length: 31 }, (_, i) => String(i + 1)), "-1"];
type Freq = Builder["freq"];
const FREQ_LABELS: Record<Freq, { label: string; unit: string }> = {
  DAILY: { label: "Daily", unit: "day(s)" },
  WEEKLY: { label: "Weekly", unit: "week(s)" },
  MONTHLY: { label: "Monthly", unit: "month(s)" },
};

/** The schedule builder's fields → its rule (null while they are incomplete). */
export function builderOf(s: {
  freq: Freq;
  interval: string;
  days: Weekday[];
  monthDay: string;
  time: string;
}): Builder | null {
  const interval = Number(s.interval);
  const [h, m] = s.time.split(":").map(Number);
  if (!/^\d{1,4}$/.test(s.interval.trim()) || interval < 1 || interval > 1000) return null;
  if (h === undefined || m === undefined || Number.isNaN(h) || Number.isNaN(m)) return null;
  if (s.freq === "WEEKLY" && !s.days.length) return null;
  return { freq: s.freq, interval, days: s.days, monthDay: Number(s.monthDay), hour: h, minute: m };
}

function RecurringWorkDialog({
  api,
  company,
  responsibility,
  onClose,
  onSaved,
}: {
  api: Api;
  company: CompanyAccess;
  responsibility: Responsibility;
  onClose: () => void;
  onSaved: () => void;
}) {
  const tz = tzOf(company);
  const [freq, setFreq] = useState<Freq>("WEEKLY");
  const [interval, setEvery] = useState("1");
  const [days, setDays] = useState<Weekday[]>([1]);
  const [monthDay, setMonthDay] = useState("1");
  const [time, setTime] = useState("09:00");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [due, setDue] = useState("");
  const [boardId, setBoardId] = useState("");
  // Boards the task can land on; a company without Work (or boards) just gets "No board".
  const boards = useQuery({ ...boardsQuery(api, company.id), retry: false });
  const boardRows = (boards.data?.data ?? []).filter((b) => !b.archived_at);
  const built = builderOf({ freq, interval, days, monthDay, time });
  const schedule = built ? builderText(built) : null;
  const first = built ? firstRun(builderRule(built), tz) : null;
  const dueOk = due === "" || /^\d{1,3}$/.test(due);
  const create = useMutation({
    mutationFn: () =>
      api.client.recurringWork.create({
        params: { co: company.id, id: responsibility.id },
        body: {
          schedule: schedule ?? "",
          board_id: boardId || null,
          task_template: {
            title: title.trim(),
            ...(description.trim() ? { description: description.trim() } : {}),
            ...(due !== "" ? { due_in_days: Number(due) } : {}),
          },
        },
      }),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (schedule && title.trim() && dueOk) create.mutate();
  };
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[640px]">
        <form onSubmit={onSubmit} className="flex flex-col gap-5" aria-label="New recurring work">
          <DialogHeader>
            <DialogTitle>New recurring work</DialogTitle>
            <DialogDescription>
              A task for "{responsibility.name}" on a schedule, in the company's time zone ({tz}).
              With a board, each run creates a task there; without one, each run is only recorded.
            </DialogDescription>
          </DialogHeader>
          <fieldset className="flex flex-col gap-4" aria-label="Schedule">
            <div className="grid gap-4 sm:grid-cols-3">
              <Field>
                <FieldLabel htmlFor="rw-freq">Repeats</FieldLabel>
                <NativeSelect
                  id="rw-freq"
                  value={freq}
                  onChange={(e) => setFreq(e.target.value as Freq)}
                >
                  {(Object.keys(FREQ_LABELS) as Freq[]).map((f) => (
                    <NativeSelectOption key={f} value={f}>
                      {FREQ_LABELS[f].label}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
              </Field>
              <Field>
                <FieldLabel htmlFor="rw-interval">Every</FieldLabel>
                <Input
                  id="rw-interval"
                  inputMode="numeric"
                  value={interval}
                  aria-describedby="rw-interval-unit"
                  onChange={(e) => setEvery(e.target.value)}
                />
                <FieldDescription id="rw-interval-unit">{FREQ_LABELS[freq].unit}</FieldDescription>
              </Field>
              <Field>
                <FieldLabel htmlFor="rw-time">Time</FieldLabel>
                <Input
                  id="rw-time"
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                />
              </Field>
            </div>
            {freq === "WEEKLY" ? (
              <Field>
                <FieldTitle id="rw-days-label">On</FieldTitle>
                <fieldset className="flex flex-wrap gap-1" aria-labelledby="rw-days-label">
                  {WEEKDAYS.map((d) => (
                    <Toggle
                      key={d}
                      size="sm"
                      variant="outline"
                      aria-label={WEEKDAY_LONG[d]}
                      pressed={days.includes(d)}
                      onPressedChange={(on) =>
                        setDays((prev) => (on ? [...prev, d] : prev.filter((x) => x !== d)))
                      }
                    >
                      {WEEKDAY_SHORT[d]}
                    </Toggle>
                  ))}
                </fieldset>
                {!days.length ? <FieldDescription>Pick at least one day.</FieldDescription> : null}
              </Field>
            ) : null}
            {freq === "MONTHLY" ? (
              <Field>
                <FieldLabel htmlFor="rw-monthday">Day of the month</FieldLabel>
                <NativeSelect
                  id="rw-monthday"
                  value={monthDay}
                  onChange={(e) => setMonthDay(e.target.value)}
                >
                  {MONTH_DAYS.map((d) => (
                    <NativeSelectOption key={d} value={d}>
                      {d === "-1" ? "Last day" : d}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
                {Number(monthDay) > 28 ? (
                  <FieldDescription>Months without this day are skipped.</FieldDescription>
                ) : null}
              </Field>
            ) : null}
            <p className="text-sm" role="status" data-slot="schedule-preview">
              {schedule ? (
                <>
                  <span className="font-medium">{describeSchedule(schedule)}</span>
                  {first ? (
                    <span className="text-muted-foreground">
                      {" "}
                      · first run {formatInZone(first, tz)}
                    </span>
                  ) : null}
                  <code className="block break-all font-mono text-xs text-muted-foreground">
                    {schedule}
                  </code>
                </>
              ) : (
                <span className="text-muted-foreground">
                  Finish the schedule to see when it runs.
                </span>
              )}
            </p>
          </fieldset>
          <Field>
            <FieldLabel htmlFor="rw-title">Task title</FieldLabel>
            <Input
              id="rw-title"
              value={title}
              required
              maxLength={200}
              onChange={(e) => setTitle(e.target.value)}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="rw-description">Task description</FieldLabel>
            <Textarea
              id="rw-description"
              rows={2}
              maxLength={2000}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="rw-board">Board</FieldLabel>
            <NativeSelect
              id="rw-board"
              value={boardId}
              onChange={(e) => setBoardId(e.target.value)}
            >
              <NativeSelectOption value="">No board (record runs only)</NativeSelectOption>
              {boardRows.map((b) => (
                <NativeSelectOption key={b.id} value={b.id}>
                  {b.name}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
          <Field>
            <FieldLabel htmlFor="rw-due">Due after (days)</FieldLabel>
            <Input
              id="rw-due"
              inputMode="numeric"
              value={due}
              placeholder="No due date"
              aria-invalid={!dueOk || undefined}
              onChange={(e) => setDue(e.target.value)}
            />
          </Field>
          {problemCode(create.error) === "schedule_invalid" ? (
            <Notice tone="danger" role="alert" title="The schedule was refused">
              The API could not use this schedule. Pick a different frequency or time.
            </Notice>
          ) : (
            <FormError what="Adding the recurring work" error={create.error} />
          )}
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!schedule || !title.trim() || !dueOk || create.isPending}
            >
              {create.isPending ? "Adding…" : "Add recurring work"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
